import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";
import { useAuth } from "@/lib/auth-context";
import { Section } from "@/components/Section";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function MagicLinkCallback() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const next = params.get("next") || "/portal";
  const [state, setState] = useState("consuming");
  const [error, setError] = useState(null);
  const { setSession } = useAuth();
  const navigate = useNavigate();
  const consumed = useRef(false);

  useEffect(() => {
    if (!token || consumed.current) return;
    consumed.current = true;
    (async () => {
      try {
        const r = await axios.post(`${BACKEND_URL}/api/auth/magic-link/consume`, { token });
        setSession(r.data.token, r.data.user);
        setState("ok");
        setTimeout(() => navigate(next, { replace: true }), 700);
      } catch (e) {
        setError(e?.response?.data?.detail || "This link is no longer valid.");
        setState("error");
      }
    })();
  }, [token, next, navigate, setSession]);

  return (
    <Section className="pt-32" testId="magic-link-callback">
      <div className="max-w-md mx-auto rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        {state === "consuming" && (
          <>
            <div className="w-12 h-12 mx-auto rounded-full bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-700">
              <Loader2 className="w-5 h-5 animate-spin" />
            </div>
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Signing you in…</h1>
          </>
        )}
        {state === "ok" && (
          <>
            <div className="w-12 h-12 mx-auto rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center text-emerald-700">
              <CheckCircle2 className="w-5 h-5" />
            </div>
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Welcome back.</h1>
            <p className="mt-1 text-sm text-slate-500">Redirecting…</p>
          </>
        )}
        {state === "error" && (
          <>
            <div className="w-12 h-12 mx-auto rounded-full bg-rose-50 border border-rose-100 flex items-center justify-center text-rose-700">
              <AlertCircle className="w-5 h-5" />
            </div>
            <h1 className="mt-4 font-display text-2xl font-bold text-slate-900">Link is no longer valid.</h1>
            <p className="mt-2 text-sm text-slate-600">{error}</p>
            <Link
              to="/login"
              className="mt-6 inline-flex items-center gap-1.5 bg-slate-900 hover:bg-slate-800 text-white rounded-full px-5 py-2.5 text-sm font-semibold transition-colors"
            >
              Request a new link
            </Link>
          </>
        )}
      </div>
    </Section>
  );
}
